import { cx } from 'emotion';
import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { ListItem, theme } from '@ublend-npm/aulaui-next';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { evaluateMaterialAction } from '@core/materials/permissions';

import MaterialVisibilityToggle from './MaterialVisibilityToggle.container';
import { listItem, hiddenListItem } from './MaterialListItem.styled';
import {
  completedListItem,
  sectionItem,
  selectedListItem,
} from './SectionListItem.styled';
import { useDrag, useDrop } from './MaterialListItem.dnd';

const CLASSES = { text: 'label' };

const SectionListItem = (props) => {
  const {
    materialId,
    hidden,
    parent,
    isInstructor,
    isStudent,
    selected,
    onReorder,
    educatorOnly,
    sectionCompleted,
    canEdit,
    canView = true,
    permissions = [],
    isRbacEnabled = false,
    hasProgressPermission = false,
    ...otherProps
  } = props;
  const listItemRef = useRef(null);
  const dragDropProps = { ...props, isFolder: false, canEdit };
  const [{ isDragging }, drag] = useDrag(dragDropProps);
  const [{ dropType }, drop] = useDrop(dragDropProps, listItemRef);

  drag(drop(listItemRef));

  // For students: check if they have toggle visibility permission to show eye icon
  const toggleVisibilityStatus =
    isStudent && isRbacEnabled
      ? evaluateMaterialAction('TOGGLE_VISIBILITY', {
          permissions,
          fallback: false,
        })
      : { allowed: false };

  const showEyeIconForStudent =
    isStudent && isRbacEnabled && toggleVisibilityStatus.allowed;
  return (
    <li
      style={{ padding: '2px 0px' }}
      ref={listItemRef}
      data-testid="materials-section-list-item"
    >
      <ListItem
        component="div"
        textTruncate
        className={cx(
          listItem,
          sectionItem,
          hasProgressPermission && sectionCompleted && completedListItem,
          hidden && hiddenListItem,
          selected && selectedListItem
        )}
        classes={CLASSES}
        rightDecoration={
          <>
            {isInstructor && !educatorOnly && canView && (
              <MaterialVisibilityToggle materialId={materialId} />
            )}
            {showEyeIconForStudent && (
              <MaterialVisibilityToggle materialId={materialId} />
            )}
            {hasProgressPermission && sectionCompleted && (
              <CheckCircleOutlineIcon
                style={{
                  color: theme.color.designSystem.textColors.green,
                  fontSize: `24px`,
                }}
                title="Progress Status"
                aria-hidden="false"
                aria-label="Material has been marked as completed"
                role="img"
                data-testid={`section-completed-icon-${materialId}`}
              />
            )}
          </>
        }
        isDraggable={canEdit}
        isDragging={canEdit ? isDragging : false}
        dropType={canEdit ? dropType : undefined}
        attributes={{ 'aria-current': selected && 'page' }}
        withTooltipInPosition="top"
        {...otherProps}
      />
    </li>
  );
};

SectionListItem.propTypes = {
  materialId: PropTypes.string.isRequired,
  hidden: PropTypes.bool.isRequired,
  parent: PropTypes.string,
  isInstructor: PropTypes.bool.isRequired,
  isStudent: PropTypes.bool.isRequired,
  selected: PropTypes.bool.isRequired,
  onReorder: PropTypes.func.isRequired,
  educatorOnly: PropTypes.bool,
  sectionCompleted: PropTypes.bool.isRequired,
  canEdit: PropTypes.bool,
  canView: PropTypes.bool,
  permissions: PropTypes.arrayOf(PropTypes.string),
  isRbacEnabled: PropTypes.bool,
  hasProgressPermission: PropTypes.bool,
};

SectionListItem.defaultProps = {
  parent: null,
  educatorOnly: false,
  canEdit: true,
  canView: true,
  permissions: [],
  isRbacEnabled: false,
  hasProgressPermission: false,
};

export default React.memo(SectionListItem);
